import React, { useState, createRef } from 'react';
import { IconButton, Menu, Box, TableBody, TableRow, Divider } from '@material-ui/core';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowLeftIcon from '@material-ui/icons/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';
import clsx from 'clsx';
import { Link, TableCell, Table, Checkbox, MenuItem, Typography } from '@rcg/ui-react';
import useStyles from './index.style';
import Button from '../Button';
import Dictionary from '../../../utils/dictionary';
import useDictionary from '../../../hooks/useDictionary';

const GuestMenu = ({
  className,
  icon = <KeyboardArrowDownIcon />,
  labelMenu = '',
  labelCancel = '',
  labelDone = '',
  labelOnLapNote = '',
  unaccompaniedMinorError = 'Children may not travel alone, must be accompanied by passenger 18yrs or older',
  minAgeToTravelAlone = 12,
  minAgeToAccompanyMinors = 18,
  menuItems,
  id = 'guestMenu',
  onChoseDone = () => {},
  disabled = false,
  disableCancelButton = false,
}) => {
  const classes = useStyles();
  const ref = createRef();
  const getDictionaryValue = useDictionary(Dictionary);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedGuests, setSelectedGuests] = useState(menuItems);
  const [preSelectedGuests, setPreselectedGuests] = useState(menuItems);

  const open = Boolean(anchorEl);

  const validateRegularAdults = (guests) => {
    return guests.some((guest) => {
      return guest.selected && guest.age >= minAgeToAccompanyMinors;
    });
  };

  const disableDoneButton = () => {
    const activeGuests = selectedGuests.filter((guest) => guest.selected);
    return activeGuests.length > 0
      ? activeGuests.some((guest) => {
          return guest.selected && guest.age < minAgeToTravelAlone;
        }) && !validateRegularAdults(activeGuests)
      : true;
  };

  const isGuestChecked = (event, index) => {
    const selected = event.target.checked;
    const selectedGuest = selectedGuests.map((guest, i) => {
      return index === i ? { ...guest, selected } : { ...guest };
    });
    setSelectedGuests(selectedGuest);
  };
  const changeOnLapSelection = (index) => {
    const selectedGuest = selectedGuests.map((guest, i) => {
      return index === i
        ? { ...guest, requiresFlightTicket: !guest.requiresFlightTicket }
        : { ...guest };
    });
    setSelectedGuests(selectedGuest);
  };
  const doneOption = () => {
    setSelectedGuests(selectedGuests);
    setPreselectedGuests(selectedGuests);
    onChoseDone(selectedGuests);
    setAnchorEl(null);
  };
  const cancelOption = () => {
    setSelectedGuests(preSelectedGuests);
    setAnchorEl(null);
    doneOption();
  };
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    event.preventDefault();
  };

  const shouldDisplayOnLapNote = selectedGuests
    ? selectedGuests.some((guest) => guest.allowOnLapOption)
    : false;

  const pasengers = selectedGuests.filter(
    (guest) => guest.selected && guest.requiresFlightTicket,
  ).length;

  return (
    <Box className={clsx(className)}>
      <Box>
        {/* TODO: It would be better if we could use button instead of link for this */}
        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
        <Link
          data-testid="guest-menu"
          component="button"
          underline="none"
          disable="true"
          aria-controls={id}
          aria-haspopup="true"
          className={`${classes.label}`}
          onClick={handleClick}>
          {pasengers}
          {` ${pasengers !== 1 ? labelMenu : getDictionaryValue(Dictionary.airPassengerLabel.key)}`}
          {icon}
        </Link>
      </Box>
      <Menu
        className={clsx(classes.root)}
        data-testid="guest-menuPop"
        id={id}
        anchorEl={anchorEl}
        getContentAnchorEl={null}
        keepMounted
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        MenuListProps={{ 'data-testid': 'menulist' }}
        onClose={cancelOption}
        open={open}>
        <MenuItem ref={ref} className={classes.menuBody}>
          <Table>
            <TableBody>
              {selectedGuests &&
                selectedGuests.map((guest, index) => {
                  const labelId = `enhanced-table-checkbox-${index}`;
                  return (
                    <TableRow key={guest.name}>
                      <TableCell width={34} className="guestSelector">
                        <Checkbox
                          disabled={disabled ? true : !guest.requiresFlightTicket}
                          inputProps={{
                            'data-testid': `guest-checkbox-${index}`,
                          }}
                          onChange={(event) => isGuestChecked(event, index)}
                          checked={guest.selected}
                        />
                      </TableCell>
                      <TableCell className="guestName" align="left" id={labelId}>
                        <Typography variant="body1">{guest.name}</Typography>
                        {guest.selected &&
                          guest.age < minAgeToTravelAlone &&
                          !validateRegularAdults(selectedGuests) && (
                            <Typography className="unaccompaniedMinorError" variant="caption">
                              {unaccompaniedMinorError}
                            </Typography>
                          )}
                      </TableCell>
                      <TableCell className="guestType" align="center">
                        {guest.allowOnLapOption ? (
                          <div className="onLapOptionSelectorContainer">
                            <div className="onLapOptionSelector">
                              <IconButton
                                data-testid="onSeatAction"
                                disabled={guest.requiresFlightTicket}
                                onClick={() => {
                                  changeOnLapSelection(index);
                                }}>
                                <KeyboardArrowLeftIcon />
                              </IconButton>

                              <Typography className="infantTypeLabel" variant="body1">
                                {guest.flightPassengerType[guest.requiresFlightTicket ? 0 : 1]}
                              </Typography>
                              <IconButton
                                data-testid="onLapAction"
                                disabled={!guest.requiresFlightTicket}
                                onClick={() => {
                                  changeOnLapSelection(index);
                                }}>
                                <KeyboardArrowRightIcon />
                              </IconButton>
                            </div>
                          </div>
                        ) : (
                          <Typography variant="body1">{guest.flightPassengerType}</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
          <Divider />
          <Box className="guestButtonWrapper">
            {shouldDisplayOnLapNote && <Typography variant="caption"> {labelOnLapNote}</Typography>}
            {!disableCancelButton && (
              <Button data-testid="cancel" onClick={cancelOption} className="cancelButton">
                {labelCancel}
              </Button>
            )}
            <Button
              data-testid="done"
              onClick={doneOption}
              className={!disableCancelButton ? 'doneButton' : ''}
              variant="contained"
              color="primary"
              disabled={disableDoneButton()}>
              {labelDone}
            </Button>
          </Box>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default GuestMenu;
